import type { Application, ApplicationChecklist, ApplicationStatus } from '../types';
import { seedApplicationLinks } from './seedEntities';

const done: ApplicationChecklist = {
  entity: 'DONE',
  owners: 'DONE',
  identity: 'DONE',
  tax: 'DONE',
  bank: 'DONE',
  wholesale: 'DONE',
  signed: 'DONE',
  funds: 'DONE',
};

const checklistFor = (status: ApplicationStatus): ApplicationChecklist => {
  switch (status) {
    case 'INVITED':
      return {
        entity: 'PENDING',
        owners: 'PENDING',
        identity: 'PENDING',
        tax: 'PENDING',
        bank: 'PENDING',
        wholesale: 'PENDING',
        signed: 'PENDING',
        funds: 'PENDING',
      };
    case 'IN_PROGRESS':
      return {
        ...done,
        identity: 'PENDING',
        wholesale: 'PENDING',
        signed: 'PENDING',
        funds: 'PENDING',
      };
    case 'SUBMITTED':
      return { ...done, funds: 'PENDING' };
    case 'KYC_REVIEW':
      return { ...done, identity: 'ISSUE', funds: 'PENDING' };
    case 'AWAITING_FUNDS':
      return { ...done, funds: 'PENDING' };
    default:
      return done;
  }
};

interface AppSpec {
  name: string;
  type: Application['applicantType'];
  contact: string;
  fundId: string;
  classId: string;
  amount: number;
  status: ApplicationStatus;
  created: string;
  holderId?: string;
  loginId?: string;
  notes?: string[];
}

const APP_SPECS: AppSpec[] = [
  {
    name: 'Bennett Nominees Pty Ltd ATF Bennett Family Trust',
    type: 'TRUST',
    contact: 'Olivia Bennett',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 300_000,
    status: 'IN_PROGRESS',
    created: '2026-10-08',
    holderId: 'h-bennett-trust',
    loginId: 'il-bennett',
    notes: ['Started from her SDF2 allocation in the investor app'],
  },
  {
    name: 'Calder Street Pty Ltd ATF Calder Super Fund',
    type: 'SMSF',
    contact: 'Hamish Calder',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 500_000,
    status: 'SUBMITTED',
    created: '2026-10-02',
  },
  {
    name: 'Wren Holdings Pty Ltd',
    type: 'COMPANY',
    contact: 'Imogen Wren',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 1_000_000,
    status: 'KYC_REVIEW',
    created: '2026-09-29',
    notes: ['GreenID: director date of birth mismatch on driver licence'],
  },
  {
    name: 'Theo & Clara Vasquez',
    type: 'INDIVIDUAL',
    contact: 'Theo Vasquez',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 250_000,
    status: 'AWAITING_FUNDS',
    created: '2026-09-25',
  },
  {
    name: 'Kestrel Family Investments Pty Ltd ATF Kestrel Trust',
    type: 'TRUST',
    contact: 'Simone Kestrel',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 750_000,
    status: 'PENDING_APPROVAL',
    created: '2026-09-20',
  },
  {
    name: 'Ingrid Solberg',
    type: 'INDIVIDUAL',
    contact: 'Ingrid Solberg',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 150_000,
    status: 'INVITED',
    created: '2026-10-14',
  },
  {
    name: 'Mehta Ventures Pty Ltd',
    type: 'COMPANY',
    contact: 'Raj Mehta',
    fundId: 'f-sdf2',
    classId: 'c-sdf2-a',
    amount: 1_500_000,
    status: 'INVITED',
    created: '2026-10-12',
    holderId: 'h-mehta-co',
    loginId: 'il-mehta',
  },
  {
    name: 'Ardent Partners',
    type: 'PARTNERSHIP',
    contact: 'Felix Ardent',
    fundId: 'f-psd',
    classId: 'c-psd-a',
    amount: 200_000,
    status: 'IN_PROGRESS',
    created: '2026-10-06',
  },
  {
    name: 'Moreau Super Pty Ltd ATF Moreau SMSF',
    type: 'SMSF',
    contact: 'Juliette Moreau',
    fundId: 'f-psd',
    classId: 'c-psd-a',
    amount: 400_000,
    status: 'AWAITING_FUNDS',
    created: '2026-10-01',
  },
  {
    name: 'Harlow Capital Pty Ltd',
    type: 'COMPANY',
    contact: 'Ben Harlow',
    fundId: 'f-core',
    classId: 'c-core-a',
    amount: 600_000,
    status: 'SUBMITTED',
    created: '2026-10-10',
  },
  {
    name: 'Quentin Ashby',
    type: 'INDIVIDUAL',
    contact: 'Quentin Ashby',
    fundId: 'f-core',
    classId: 'c-core-a',
    amount: 100_000,
    status: 'REJECTED',
    created: '2026-09-12',
    notes: ['Did not meet wholesale test; certificate expired 2025'],
  },
  {
    name: 'Larkspur Pty Ltd ATF Larkspur Trust',
    type: 'TRUST',
    contact: 'Nina Larkspur',
    fundId: 'f-psd',
    classId: 'c-psd-a',
    amount: 300_000,
    status: 'ISSUED',
    created: '2026-08-20',
  },
];

export function buildApplications(): Application[] {
  return APP_SPECS.map((spec, index) => ({
    id: `app-${index + 1}`,
    reference: `APP-26-${String(1040 + index)}`,
    fundId: spec.fundId,
    classId: spec.classId,
    ...seedApplicationLinks(`app-${index + 1}`, spec.holderId),
    applicantName: spec.name,
    applicantType: spec.type,
    contactName: spec.contact,
    email:
      `${spec.contact.split(' ')[0]}.${spec.contact.split(' ').slice(-1)[0]}@example.com`.toLowerCase(),
    amount: spec.amount,
    status: spec.status,
    createdAt: spec.created,
    submittedAt: ['INVITED', 'IN_PROGRESS'].includes(spec.status) ? undefined : spec.created,
    fundsReceivedAt: ['PENDING_APPROVAL', 'ISSUED'].includes(spec.status)
      ? spec.created
      : undefined,
    checklist: checklistFor(spec.status),
    notes: spec.notes ?? [],
    investorLoginId: spec.loginId,
  }));
}
